import { Button, Flash, Select, Spinner } from 'AWS-UI-Components-React';
import React from 'react';
import { extractErrorMessage } from '../../utilities';
import { buildCognosComponentIndex, getMultiComponentCognosIds } from '../../utils/cognosMultiComponent';
import logger from '../../utils/logging';
import { ChannelItems } from '../const/dropdown_elements';
import { getJSClient } from '../js_client';
import { AccuracyInfo } from './accuracy_benchmark_info';
import { MappingReview } from './mapping_review';
import { MappingTable } from './mapping_table';
import { TaxAdjustmentsAlert } from './tax_adjustments_alert';

export class MapperForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      payload: {
        marketplace: '',
        channel: '',
        component: '',
      },
      componentSelected: false,
      marketplaceSelected: false,
      configReady: false,
      components: [],
      marketplaces: [],
      cognosAccounts: [],
      etlConfig: [],
      key: 0,
      reviewerKey: 0,
      stageInProgress: false,
      stageSucceeded: false,
      stageFailed: false,
      stagingArea: props.identity.username,
      stagedMappings: [],
      filteredStagedMappings: [],
      mappingPullFailed: false,
      reviewInProgress: false,
      unstageInProgress: false,
      unstageSucceeded: false,
      unstageFailed: false,
      publishInProgress: false,
      publishSucceeded: false,
      publishFailed: false,
      reviewFailureMessage: '',
      mappingsToUnstage: [],
      versionConflicts: [],
      readOnly: this.props.readOnly,
      componentConfigMap: new Map(),
      cognosComponentIndex: new Map(),
      multiComponentCognosIds: new Set(),
    };
    this.stagingSubComponent = '';
    this.config = {};
  }

  sameKey = (a, b) =>
    a.marketplace === b.marketplace &&
    a.channel === b.channel &&
    a.component === b.component &&
    a.subComponent === b.subComponent;

  componentWillMount = () => {
    document.title = "Phoenix : Mapper";
    this.client = getJSClient();
    this.initialClientTasks();
  };

  initialClientTasks = () => {
    this.loadStagedMappings();
    this.props.ignoreInProgress && this.setIgnoreExcludeComponent(this.props.ignoredComponent);
    this.props.excludeInProgress && this.setIgnoreExcludeComponent(this.props.excludedComponent);
    this.client.getConfig(this.onConfigFetchSuccess, this.onConfigFetchFailure, 'filterIgnored=true');
  };

  loadStagedMappings = () => {
    this.client.getMappings(this.onMappingPullSuccess, this.onMappingPullFailure, {
      version: `SA-${this.state.stagingArea}`,
    });
  };

  onMappingPullSuccess = (response) => {
    this.setState({
      stagedMappings: response.mappings,
      filteredStagedMappings: this.filterMappings(this.state.payload, response.mappings),
    });
  };

  onMappingPullFailure = (response) => {
    console.error(response);
    this.setState({ mappingPullFailed: true, failureMessage: response });
  };

  onConfigFetchSuccess = (response) => {
    try {
      this.config.components = JSON.parse(response.componentConfig);
      this.config.marketplaces = JSON.parse(response.marketplaceConfig);
      this.config.cognos = JSON.parse(response.cognosConfig);
      const etlConfig = JSON.parse(response.etlConfig);
      const components = [];
      const flashbackColumns = {};
      const oWbrCpColumns = {};
      for (const component of this.config.components) {
        const id = component.id.trim();
        oWbrCpColumns[id] = component.owbrCpColumn;
        components.push({
          label: id,
          value: id,
          id,
        });
        const componentColumnsOptions = [];
        if (component.columns) {
          for (const column of component.columns) {
            componentColumnsOptions.push({
              label: column,
              value: column,
              id: column,
            });
          }
        }
        flashbackColumns[id] = componentColumnsOptions;
      }

      const componentConfigMap = new Map();
      for(const component of this.config.components){
        if(component.baseFactor){
          const row = {
            component: component.component,
            cpDataSource: component.cpDataSource,
            base_factor: component.baseFactor,
            reallocation_grain: component.reallocationGrain,
            comments: component.comments
          };
          componentConfigMap.set(component.component, [row]);
        }
      }

      const cognosAccountOptions = [];
      for (const cognosAccount of this.config.cognos) {
        cognosAccountOptions.push({
          label: `${cognosAccount.id} - ${cognosAccount.detail}`,
          value: `${cognosAccount.id} - ${cognosAccount.detail}`,
          id: cognosAccount.id,
          excluded: cognosAccount.excluded || false,
          comment: cognosAccount.comment,
          accuracyComponent: cognosAccount.accuracyComponent || [],
          cognosOnlyComponent: cognosAccount.cognosOnlyComponent || [],
        });
      }
      this.config.cognosAccountOptions = cognosAccountOptions;

      const cognosComponentIndex = buildCognosComponentIndex(this.config.cognos);
      const multiComponentCognosIds = getMultiComponentCognosIds(cognosComponentIndex);

      const marketplaces = [
        {
          label: 'All',
          value: '*',
          id: '*',
        },
      ];
      for (const marketplace of this.config.marketplaces) {
        marketplaces.push({
          label: `${marketplace.id.trim()} - ${marketplace.detail.trim()}`,
          value: marketplace.id.trim(),
          id: marketplace.id.trim(),
        });
      }

      const etlConfigOptionMap = {};
      for (const config of etlConfig) {
        const key = config.id.trim();
        const value = [];
        if (config.columns) {
          for (const etlColumn of config.columns) {
            value.push({
              label: etlColumn,
              value: etlColumn,
              id: etlColumn,
            });
          }
        }
        etlConfigOptionMap[key] = value;
      }

      this.setState({
        configReady: true,
        components,
        marketplaces,
        flashbackColumns,
        oWbrCpColumns,
        componentConfigMap,
        etlConfig: etlConfigOptionMap,
        cognosComponentIndex,
        multiComponentCognosIds,
      });
    } catch (e) {
      logger.error('Error parsing config response', e);
    }
  };

  onConfigFetchFailure = (errorResponse) => {
    console.error(errorResponse);
  };

  filterMappings = (payload, mappings) => {
    const filteredMappings = [];
    for (const mapping of mappings) {
      if (
        mapping.component === payload.component &&
        this.equalOrBlank(mapping.marketplace, payload.marketplace) &&
        this.equalOrBlank(mapping.channel, payload.channel)
      ) {
        filteredMappings.push(mapping);
      }
    }
    return filteredMappings;
  };

  equalOrBlank = (a, b) => a === b || !(a || b);

  setComponent = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    let componentSelected = false;
    newPayload.component = chosen.detail.selectedId;
    newPayload.marketplace = '';
    newPayload.channel = '';
    if (chosen.detail.selectedId) {
      componentSelected = true;
    }
    this.props.updateToolsContent(<AccuracyInfo />);
    this.setState({
      payload: newPayload,
      key: this.state.key + 1,
      componentSelected,
      filteredStagedMappings: this.filterMappings(newPayload, this.state.stagedMappings),
    });
  };

  setIgnoreExcludeComponent = (componentName) => {
    if (this.props.ignoreInProgress || this.props.excludeInProgress) {
      const newPayload = Object.assign({}, this.state.payload);
      let componentSelected = true;
      newPayload.component = componentName;
      newPayload.marketplace = '';
      newPayload.channel = '';
      this.setState({
        payload: newPayload,
        key: this.state.key + 1,
        componentSelected,
        filteredStagedMappings: this.filterMappings(newPayload, this.state.stagedMappings),
      });
    }
  };

  recreateMappingTable = () => (
    <MappingTable
      key={this.state.key}
      identity={this.props.identity}
      filters={this.state.payload}
      stagingArea={this.state.stagingArea}
      readOnly={this.state.readOnly}
      stageChange={this.stageChange}
      unstageChange={this.unstageChange}
      updateTools={this.props.updateTools}
      featureList={this.props.featureList}
      component={this.state.payload.component}
      marketplace={this.state.payload.marketplace}
      channel={this.state.payload.channel}
      oWbrCpColumns={this.state.oWbrCpColumns}
      flashbackColumns={this.state.flashbackColumns[this.state.payload.component]}
      cognosConfig={this.config.cognosAccountOptions}
      etlConfig={this.state.etlConfig}
      ignoreInProgress={this.props.ignoreInProgress}
      ignoredAccount={this.props.ignoredAccount}
      excludeInProgress={this.props.excludeInProgress}
      excludedAccount={this.props.excludedAccount}
      multiComponentCognosIds={this.state.multiComponentCognosIds}
      cognosComponentIndex={this.state.cognosComponentIndex}
    />
  );

  stageChange = (change, isDelete) => {
    if (change.component === change.subComponent) {
      change.subComponent = undefined;
    } else {
      this.stagingSubComponent = change.subComponent;
    }
    const changes = isDelete ? [] : [change];
    const deletes = isDelete ? [change] : [];
    this.client.stageMappings(this.onSuccessfulStage, this.onFailedStage, {
      stagingArea: this.state.stagingArea,
      saveList: changes,
      deleteList: deletes,
    });
  };

  onSuccessfulStage = () => {
    this.setState({ key: this.state.key + 1 });
    this.loadStagedMappings();
  };

  onFailedStage = (payload) => {
    let message = '';
    if (payload.response && payload.response.data && payload.response.data.message) {
      message = `: ${payload.response.data.message}`;
    }
    this.setState({ stageFailed: true, failureMessage: message, failedStagedComponent: this.stagingSubComponent });
  };

  unstageChange = (change) => {
    if (change.component === change.subComponent) {
      change.subComponent = undefined;
    }
    this.setState({ mappingsToUnstage: [change] });
    this.client.unstageMappings(this.onSuccessfulUnstage, this.onFailedUnstage, {
      stagingArea: this.state.stagingArea,
      unstageList: [change],
    });
  };

  unstageChanges = (mappings) => {
    this.setState({
      unstageInProgress: true,
      unstageSucceeded: false,
      unstageFailed: false,
      mappingsToUnstage: mappings,
    });
    for (const item of mappings) {
      if (item.component === item.subComponent) {
        item.subComponent = undefined;
      }
    }
    this.client.unstageMappings(this.onSuccessfulUnstage, this.onFailedUnstage, {
      stagingArea: this.state.stagingArea,
      unstageList: mappings,
    });
  };

  onSuccessfulUnstage = () => {
    const versionConflicts = this.state.versionConflicts.filter((conflict) =>
      this.state.mappingsToUnstage.some((mapping) => {
        this.sameKey(conflict, mapping);
      })
    );
    this.setState({
      unstageInProgress: false,
      unstageSucceeded: true,
      unstageFailed: false,
      versionConflicts,
      mappingsToUnstage: [],
      key: this.state.key + 1,
      reviewerKey: this.state.reviewerKey + 1,
    });
    this.loadStagedMappings();
  };

  onFailedUnstage = (payload) => {
    logger.error('Fail to unstage mappings', payload);
    let message = extractErrorMessage(payload);
    if (message) {
      message = `: ${message}`;
    }
    this.setState({
      unstageInProgress: false,
      unstageSucceeded: false,
      unstageFailed: true,
      reviewFailureMessage: message,
    });
  };

  publishFilteredMappings = () => {
    this.publishChanges(this.state.filteredStagedMappings);
  };

  publishChanges = (mappings) => {
    this.setState({ publishInProgress: true, publishSucceeded: false, publishFailed: false });
    for (const item of mappings) {
      if (item.component === item.subComponent) {
        item.subComponent = undefined;
      }
    }
    this.client.publishMappings(this.onSuccessfulPublish, this.onFailedPublish, {
      stagingArea: this.state.stagingArea,
      publishList: mappings,
    });
  };

  onSuccessfulPublish = (response) => {
    this.setState({
      publishInProgress: false,
      publishSucceeded: true,
      publishFailed: false,
      versionConflicts: response.versionConflicts,
      key: this.state.key + 1,
      reviewerKey: this.state.reviewerKey + 1,
    });
    this.loadStagedMappings();

    if(typeof this.props.onMappingPublish === 'function'){
      this.props.onMappingPublish();
    }
  };

  onFailedPublish = (payload) => {
    logger.error('Fail to publish mappings', payload);
    let message = extractErrorMessage(payload);
    if (message) {
      message = `: ${message}`;
    }
    this.setState({
      publishInProgress: false,
      publishSucceeded: false,
      publishFailed: true,
      reviewFailureMessage: message,
    });
  };

  resetReviewFlash = () => {
    this.setState({
      unstageInProgress: false,
      unstageSucceeded: false,
      unstageFailed: false,
      publishInProgress: false,
      publishSucceeded: false,
      publishFailed: false,
    });
  };

  setMarketplace = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    let marketplaceSelected = false;
    if (chosen.detail.selectedOption) {
      newPayload.marketplace = chosen.detail.selectedOption.id;
      marketplaceSelected = true;
    } else {
      newPayload.marketplace = '';
    }
    newPayload.channel = '';
    this.setState({
      payload: newPayload,
      key: this.state.key + 1,
      marketplaceSelected,
      selectedMarketplaceOption: chosen,
      filteredStagedMappings: this.filterMappings(newPayload, this.state.stagedMappings),
    });
  };

  setChannelType = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.channel = chosen.detail.selectedOption.id;
    this.setState({
      payload: newPayload,
      key: this.state.key + 1,
      filteredStagedMappings: this.filterMappings(newPayload, this.state.stagedMappings),
    });
  };

  beginReview = () => {
    this.flashDismissed();
    this.resetReviewFlash();
    this.setState({ reviewInProgress: true });
  };

  mappingReviewer = () => (
    <MappingReview
      cognosConfig={this.config.cognosAccountOptions}
      key={this.state.reviewerKey}
      onClose={this.onCloseReviewer}
      publishChanges={this.publishChanges}
      publishInProgress={this.state.publishInProgress}
      publishSucceeded={this.state.publishSucceeded}
      publishFailed={this.state.publishFailed}
      resetReviewFlash={this.resetReviewFlash}
      reviewFailureMessage={this.state.reviewFailureMessage}
      stagingArea={this.state.stagingArea}
      unstageChanges={this.unstageChanges}
      unstageInProgress={this.state.unstageInProgress}
      unstageSucceeded={this.state.unstageSucceeded}
      unstageFailed={this.state.unstageFailed}
      versionConflicts={this.state.versionConflicts}
    />
  );

  onCloseReviewer = () => {
    this.setState({ reviewInProgress: false, key: this.state.key + 1 });
  };

  getFailedStagingComponent = () => {
    return this.state.failedStagedComponent.length ? 'SubComponent ' + this.state.failedStagedComponent : 'Component ' + this.state.payload.component;
  };

  clearFilters = () => {
    const newPayload = {
      component: '',
      marketplace: '',
      channel: '',
    };
    this.flashDismissed();
    this.setState({
      payload: newPayload,
      componentSelected: false,
      marketplaceSelected: false,
      key: this.state.key + 1,
      filteredStagedMappings: this.filterMappings(newPayload, this.state.stagedMappings),
    });
  };

  flashDismissed = () => {
    this.setState({ stageFailed: false, mappingPullFailed: false });
  };

  render() {
    return (
      <div>
        {this.state.reviewInProgress && this.mappingReviewer()}
        {!this.props.ignoreInProgress && !this.props.excludeInProgress && (
        <div className="awsui-util-container">
          <div className="awsui-util-container-header">
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">Accuracy Mappings</div>
              {!this.state.configReady && (
                <div className="awsui-util-action-stripe-group">
                  <Spinner size="big" />
                </div>
              )}
              {this.state.configReady && (
                <div className="awsui-util-action-stripe-group">
                  <Select
                    key={this.state.key}
                    placeholder="Select a component"
                    filteringType="auto"
                    filteringLabel="Filter options"
                    enableFiltering
                    options={this.state.components}
                    onChange={this.setComponent}
                    selectedId={this.state.payload.component}
                    data-cy="mapper-component-button"
                  />
                </div>
              )}
              {this.state.configReady && (
                <div className="awsui-util-action-stripe-group">
                  <Select
                    key={this.state.key}
                    placeholder="Marketplace Override"
                    filteringType="auto"
                    filteringLabel="Filter options"
                    enableFiltering
                    options={this.state.marketplaces}
                    onChange={this.setMarketplace}
                    disabled={!this.state.componentSelected}
                    selectedId={this.state.payload.marketplace}
                    data-cy="mapper-marketplace-button"
                  />
                </div>
              )}
              {this.state.configReady && (
                <div className="awsui-util-action-stripe-group">
                  <Select
                    key={this.state.key}
                    placeholder="Channel Override"
                    options={ChannelItems}
                    onChange={this.setChannelType}
                    disabled={!this.state.componentSelected || !this.state.marketplaceSelected}
                    selectedId={this.state.payload.channel}
                    data-cy="mapper-channel-button"
                  />
                </div>
              )}
              {this.state.configReady && (
                <div className="awsui-util-action-stripe-group">
                  <div style={{ paddingTop: '0.5rem' }}>
                    <Button text="Clear" onClick={this.clearFilters} />
                  </div>
                </div>
              )}
              {this.state.configReady && !this.state.readOnly && (
                <div className="awsui-util-action-stripe-group">
                  <div style={{ paddingTop: '0.5rem' }}>
                    <Button
                      text={`Review All Changes${
                        this.state.stagedMappings.length ? ` (${this.state.stagedMappings.length})` : ''
                      }`}
                      disabled={!this.state.stagedMappings.length}
                      onClick={this.beginReview}
                    />
                  </div>
                </div>
              )}
              {this.state.configReady && !this.state.readOnly && (
                <div className="awsui-util-action-stripe-group">
                  <div style={{ paddingTop: '0.5rem' }}>
                    <Button
                      text={`Publish${this.state.payload.component ? ` ${this.state.payload.component}` : ''} Changes${
                        this.state.filteredStagedMappings.length ? ` (${this.state.filteredStagedMappings.length})` : ''
                      }`}
                      variant="primary"
                      disabled={!this.state.filteredStagedMappings.length || this.state.stageFailed}
                      onClick={this.publishFilteredMappings}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          {this.state.stageFailed && (
            <Flash
              type="error"
              content={`Failed to Stage Mappings for ${this.getFailedStagingComponent()} ${this.state.failureMessage}`}
              dismissible
              onDismiss={this.flashDismissed}
            />
          )}
          {this.state.unstageFailed && (
            <Flash
              type="error"
              content={`Failed to Revert Mappings${this.state.failureMessage}`}
              dismissible
              onDismiss={this.flashDismissed}
            />
          )}
          {this.state.mappingPullFailed && (
            <Flash
              type="error"
              content={`Failed to pull staged mappings: ${this.state.failureMessage}`}
              dismissible
              onDismiss={this.flashDismissed}
            />
          )}
          <TaxAdjustmentsAlert
            component={this.state.payload.component}
            loading={!this.state.configReady}
          />
          {this.state.componentSelected && this.state.configReady && this.recreateMappingTable()}
        </div>
      )}
      {this.props.ignoreInProgress && this.recreateMappingTable()}
      {this.props.excludeInProgress && this.recreateMappingTable()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/mapper/mapper_form.jsx